import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes, FaChevronDown, FaRegHeart } from 'react-icons/fa';
import { api } from '../services/api';
import { fetchWithCache } from '../services/cache';
import LazyImage from './LazyImage';

const HeaderContainer = styled.header`
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.small};
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const Nav = styled.nav`
  max-width: ${({ theme }) => theme.breakpoints.large};
  margin: 0 auto;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
`;

const LogoImage = styled.div`
  width: 50px;
  height: 50px;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const LogoText = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  font-family: ${({ theme }) => theme.fonts.heading};
`;

const Menu = styled.ul`
  display: flex;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    padding: 1rem;
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
`;

const MenuItem = styled.li`
  position: relative;
`;

const StyledLink = styled(Link)`
  color: ${({ theme, $isActive }) => ($isActive ? theme.colors.secondary : theme.colors.text)};
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '500')};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &:hover {
    color: ${({ theme }) => theme.colors.secondary};
  }
`;

const DropdownButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme, $isActive }) => ($isActive ? theme.colors.secondary : theme.colors.text)};
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '500')};
  font-size: 1rem;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &:hover {
    color: ${({ theme }) => theme.colors.secondary};
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  min-width: 200px;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  border-radius: 4px;
  padding: 0.5rem 0;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  z-index: 1001;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: static;
    box-shadow: none;
    padding-left: 1rem;
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 0.8rem 1rem;
  color: ${({ theme }) => theme.colors.text};
  &:hover {
    background: #f9f9f9;
    color: ${({ theme }) => theme.colors.secondary};
  }
`;

const MobileIcon = styled.div`
  display: none;
  font-size: 1.5rem;
  cursor: pointer;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const MobileDonateItem = styled(MenuItem)`
  display: none;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

export const DonateButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background-color: #1a6fc4;
  color: white !important;
  padding: 0.5rem 1.1rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: opacity 0.2s;
  white-space: nowrap;
  &:hover {
    opacity: 0.88;
    color: white !important;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: center;
    margin-top: 0.5rem;
  }
`;

const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-left: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-left: 0;
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [headerData, setHeaderData] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchWithCache('homeData', () => api.get('/home'), (data) => {
      if (data && data.header) {
        setHeaderData(data.header);
      }
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = () => setIsOpen(!isOpen);
  const toggleDropdown = (e) => {
      e.preventDefault();
      setDropdownOpen(!dropdownOpen);
  };

  const mainLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
  ];

  const programLinks = [
    { name: 'Workshops', path: '/workshops' },
    { name: 'Outreach', path: '/outreach' },
  ];

  const endLinks = [
    { name: 'Team', path: '/team' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' },
  ];

  const isProgramActive = programLinks.some(link => location.pathname === link.path);

  return (
    <HeaderContainer>
      <Nav>
        <LogoLink to="/">
          {headerData?.logo && (
            <LogoImage>
              <LazyImage src={headerData.logo} alt="Logo" />
            </LogoImage>
          )}
          <LogoText>{headerData?.title || '26Minutes Ministry'}</LogoText>
        </LogoLink>
        <MobileIcon onClick={toggle}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </MobileIcon>
        <NavGroup>
        <Menu $isOpen={isOpen}>
          {mainLinks.map((link) => (
            <MenuItem key={link.name}>
              <StyledLink 
                to={link.path} 
                $isActive={location.pathname === link.path}
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </StyledLink>
            </MenuItem>
          ))}
          
          <MenuItem ref={dropdownRef}>
            <DropdownButton 
                onClick={toggleDropdown} 
                $isActive={isProgramActive}
            >
                Programs <FaChevronDown size={12} />
            </DropdownButton>
            <DropdownContent $isOpen={dropdownOpen}>
                {programLinks.map(link => (
                    <DropdownItem 
                        key={link.name} 
                        to={link.path}
                        onClick={() => {
                            setIsOpen(false);
                            setDropdownOpen(false);
                        }}
                    >
                        {link.name}
                    </DropdownItem>
                ))}
            </DropdownContent>
          </MenuItem>

          {endLinks.map((link) => (
            <MenuItem key={link.name}>
              <StyledLink 
                to={link.path} 
                $isActive={location.pathname === link.path}
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </StyledLink>
            </MenuItem>
          ))}
          <MobileDonateItem>
            <DonateButton to="/donate" onClick={() => setIsOpen(false)}>
              Give <FaRegHeart size={15} />
            </DonateButton>
          </MobileDonateItem>
        </Menu>
        <NavRight>
          <DonateButton to="/donate">
            Give <FaRegHeart size={15} />
          </DonateButton>
        </NavRight>
        </NavGroup>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;
